import type * as PdfjsNs from 'pdfjs-dist';
import type * as PdfLibNs from 'pdf-lib';
import { detectWithRegex } from '../../detect/pii/regex';
import { detectFormFields } from '../../detect/pii/forms';
import { detectNames } from '../../detect/pii/names';
import { detectLanguage, type DetectedLanguage } from '../../detect/pii/language';
import { mergeDetections } from '../../detect/pii/merge';
import { detectPdfMetadata, type PdfMetaResult } from '../../detect/pdfMeta';
import type { ImageTextHit, PiiType, TextDetection } from '../../detect/pii/types';
import { recognize, preprocessForOcr, wordsInRange, type WordBox } from '../../detect/ocr';
import { ok, err, type Result } from '../../lib/types';
import type { DispatchOpts } from '../image';

export type PdfTextItem = {
  text: string;
  /** Char offset in the page's combined text. */
  start: number;
  end: number;
  /** pdfjs transform matrix [a, b, c, d, e, f] in PDF user space. */
  transform: readonly [number, number, number, number, number, number];
  /** PDF-unit width of the rendered text run. */
  width: number;
  /** PDF-unit height of the rendered text run. */
  height: number;
};

export type PdfPageInfo = {
  index: number;
  /** PDF user-unit width / height. */
  widthPdf: number;
  heightPdf: number;
  /** Page rotation in degrees (0 / 90 / 180 / 270). */
  rotation: number;
  /** Concatenated text content for the page (regex/NER input). */
  text: string;
  items: PdfTextItem[];
  detections: TextDetection[];
  /** OCR-derived hits for image-only pages (empty `items`/`text`). */
  imageTextHits?: ImageTextHit[];
};

export type PdfDispatchValue = {
  kind: 'pdf';
  filename: string;
  outputFilename: string;
  pages: PdfPageInfo[];
  metadata: PdfMetaResult;
  /** Original file bytes — kept around for re-render at confirm time. */
  pdfBytes: Uint8Array;
  language?: DetectedLanguage;
};

type PdfjsModule = typeof PdfjsNs;

let pdfjsPromise: Promise<PdfjsModule> | null = null;

const loadPdfjs = async (): Promise<PdfjsModule> => {
  // The default pdfjs-dist v5 build uses APIs that aren't in iOS Safari
  // until 18.4 (Iterator helpers, Promise.withResolvers). The legacy
  // build polyfills these via core-js bundled inline, but those polyfills
  // don't reliably install in older Safari worker contexts. We sidestep
  // that by pointing pdfjs at a wrapper worker that imports our explicit
  // polyfill before the legacy worker module evaluates.
  pdfjsPromise ??= (async () => {
    const mod = await import('pdfjs-dist/legacy/build/pdf.mjs');
    const workerMod = await import('./pdfWorkerEntry?worker&url');
    mod.GlobalWorkerOptions.workerSrc = workerMod.default;
    return mod;
  })();
  return pdfjsPromise;
};

type PdfLibModule = typeof PdfLibNs;

let pdfLibPromise: Promise<PdfLibModule> | null = null;

const loadPdfLib = async (): Promise<PdfLibModule> => {
  pdfLibPromise ??= import('pdf-lib');
  return pdfLibPromise;
};

type RawTextItem = {
  str: string;
  transform: number[];
  width: number;
  height: number;
  hasEOL?: boolean;
};

const isItem = (x: unknown): x is RawTextItem => {
  if (!x || typeof x !== 'object') return false;
  const i = x as Partial<RawTextItem>;
  return typeof i.str === 'string' && Array.isArray(i.transform);
};

/**
 * Build the page's joined text and return per-item char ranges.
 *
 * Detects table-style column gaps: when two adjacent items share a
 * baseline but the second starts well to the right of the first item's
 * end, we insert a TAB between them in the joined buffer. The
 * form-field detector accepts `\t` as a label/value separator, so
 * "Social Security Number    900-44-7281" laid out as two table cells
 * becomes "Social Security Number\t900-44-7281" — which the SSN/DL/
 * passport form-field rules can finally match. Empty-string spacer
 * items (some PDFs emit a tiny zero-width item between columns) are
 * also taken as a column-break signal.
 */
const extractPageText = (rawItems: readonly unknown[]): { text: string; items: PdfTextItem[] } => {
  const out: PdfTextItem[] = [];
  let buf = '';
  let prev: RawTextItem | null = null;
  for (const raw of rawItems) {
    if (!isItem(raw)) continue;

    if (raw.str === '') {
      // Empty marker — many fillable forms emit a zero-width item at
      // column boundaries. Treat as a column break only when it's not
      // an EOL marker (those flow through the explicit-newline branch
      // below at the next non-empty item).
      if (raw.hasEOL) {
        if (!buf.endsWith('\n')) buf += '\n';
      } else if (!buf.endsWith('\t') && !buf.endsWith('\n')) {
        buf += '\t';
      }
      prev = raw;
      continue;
    }

    if (prev) {
      const prevY = prev.transform[5] ?? 0;
      const curY = raw.transform[5] ?? 0;
      const prevX = prev.transform[4] ?? 0;
      const curX = raw.transform[4] ?? 0;
      const sameLine = Math.abs(prevY - curY) < Math.max(1, raw.height * 0.5);
      const prevEnd = prevX + prev.width;
      const gap = curX - prevEnd;
      const fontSize = Math.max(raw.height, prev.height, 1);
      // 0.8x font size is enough of a gap to be a column boundary, not
      // intra-sentence spacing (regular word gaps run ~0.25x). The
      // earlier 1.5x threshold missed common table layouts where the
      // label and value sit closer together.
      if (sameLine && gap > fontSize * 0.8) {
        // Replace the trailing single-space joiner we appended after
        // prev with a tab — that's the actual column boundary, and the
        // form-field detector keys off `\t` specifically.
        if (buf.endsWith(' ')) buf = buf.slice(0, -1);
        if (!buf.endsWith('\t') && !buf.endsWith('\n')) buf += '\t';
      }
    }

    const start = buf.length;
    buf += raw.str;
    out.push({
      text: raw.str,
      start,
      end: buf.length,
      transform: [
        raw.transform[0] ?? 1,
        raw.transform[1] ?? 0,
        raw.transform[2] ?? 0,
        raw.transform[3] ?? 1,
        raw.transform[4] ?? 0,
        raw.transform[5] ?? 0,
      ],
      width: raw.width,
      height: raw.height,
    });
    if (raw.hasEOL) buf += '\n';
    else buf += ' ';
    prev = raw;
  }
  return { text: buf, items: out };
};

/** Subset of pdfjs annotation shape we consume. The library exposes
 *  these as `unknown[]`; we narrow at runtime. */
type WidgetAnnotation = {
  /** Numeric annotation type. WIDGET = 20 in pdfjs. */
  annotationType?: number;
  fieldName?: string | null;
  fieldValue?: string | string[] | null;
  fieldType?: string | null;
  rect?: number[] | null;
  hidden?: boolean;
  readOnly?: boolean;
};

/** pdfjs AnnotationType.WIDGET. Source: pdfjs-dist build/pdf.mjs. */
const ANNOTATION_TYPE_WIDGET = 20;

const isWidget = (a: unknown): a is WidgetAnnotation =>
  typeof a === 'object' && a !== null;

/**
 * Collect filled AcroForm field values (and their on-page rectangles)
 * from a PDF page's annotations. PDFs with fillable forms commonly
 * store user-entered values as Widget annotations, NOT in the page
 * content stream — so getTextContent returns the form template
 * ("Zip:") but not the filled value ("02128"). We surface them as
 * synthetic text items so the existing detection + redaction pipeline
 * picks them up.
 */
type FormField = {
  name: string;
  value: string;
  rect: readonly [number, number, number, number];
};

const collectFormFields = (annotations: readonly unknown[]): FormField[] => {
  const out: FormField[] = [];
  for (const a of annotations) {
    if (!isWidget(a)) continue;
    const w = a;
    if (w.annotationType !== ANNOTATION_TYPE_WIDGET) continue;
    // Only text-input fields ('Tx'). Buttons/checkboxes/signatures
    // surface as Widget but their `fieldValue` is "Yes"/"Off" or a
    // signature blob, not user-entered PII.
    if (w.fieldType && w.fieldType !== 'Tx') continue;
    if (w.hidden) continue;
    const name = typeof w.fieldName === 'string' ? w.fieldName.trim() : '';
    if (!name) continue;
    let value: string;
    if (typeof w.fieldValue === 'string') value = w.fieldValue;
    else if (Array.isArray(w.fieldValue)) value = w.fieldValue.join(' ');
    else continue;
    value = value.trim();
    if (!value) continue;
    const rect = w.rect;
    if (!Array.isArray(rect) || rect.length !== 4) continue;
    const [x1, y1, x2, y2] = rect;
    if (
      typeof x1 !== 'number' ||
      typeof y1 !== 'number' ||
      typeof x2 !== 'number' ||
      typeof y2 !== 'number'
    ) {
      continue;
    }
    if (x2 <= x1 || y2 <= y1) continue;
    out.push({ name, value, rect: [x1, y1, x2, y2] });
  }
  return out;
};

/** Append synthetic text items for each filled form field. The item's
 *  transform is built from the field's annotation rectangle so the
 *  redaction pass (which uses item.transform to compute canvas
 *  coordinates) lays the black box exactly over the printed value. */
const appendFormFieldItems = (
  base: { text: string; items: PdfTextItem[] },
  fields: readonly FormField[],
): { text: string; items: PdfTextItem[] } => {
  if (fields.length === 0) return base;
  let { text } = base;
  const items = [...base.items];
  if (text.length > 0 && !text.endsWith('\n')) text += '\n';
  for (const f of fields) {
    text += `${f.name}: `;
    const start = text.length;
    text += f.value;
    const end = text.length;
    text += '\n';
    const [x1, y1, x2, y2] = f.rect;
    items.push({
      text: f.value,
      start,
      end,
      transform: [x2 - x1, 0, 0, y2 - y1, x1, y1],
      width: x2 - x1,
      height: y2 - y1,
    });
  }
  return { text, items };
};

const outputFilename = (): string => 'redacted.pdf';

/**
 * Dispatch a PDF: parse it, extract text + items per page, run regex
 * detection per page, read metadata, and return everything the redactor
 * will need at confirm time.
 */
/** Page is treated as "image-only" (OCR candidate) when its extracted
 *  text content is below this many printable characters. */
const PAGE_OCR_THRESHOLD = 10;
/** Scale at which we rasterise image-only pages for OCR. Higher values
 *  improve recognition accuracy but quadratically increase memory; this
 *  is a balance that works on iPhone WebKit's tab budget. */
const PAGE_OCR_SCALE = 2;

const liftPdfOcr = (
  words: readonly WordBox[],
  canvasWidth: number,
  canvasHeight: number,
  detections: { type: PiiType; text: string; start: number; end: number; confidence: number }[],
): ImageTextHit[] => {
  if (canvasWidth <= 0 || canvasHeight <= 0) return [];
  const out: ImageTextHit[] = [];
  for (const d of detections) {
    const overlapping = wordsInRange(words, d.start, d.end);
    if (overlapping.length === 0) continue;
    const rects = overlapping.map((w) => ({
      x: w.bbox.x / canvasWidth,
      y: w.bbox.y / canvasHeight,
      w: w.bbox.w / canvasWidth,
      h: w.bbox.h / canvasHeight,
    }));
    out.push({
      type: d.type,
      text: d.text,
      rects,
      confidence: d.confidence,
      source: 'ocr',
    });
  }
  return out;
};

export const dispatchPdf = async (
  file: File,
  opts: DispatchOpts = {},
): Promise<Result<PdfDispatchValue>> => {
  let pdfjs: PdfjsModule;
  try {
    pdfjs = await loadPdfjs();
  } catch (cause) {
    return err({
      code: 'PDFJS_LOAD_FAILED',
      message: 'Could not load the PDF parser. Try refreshing the page.',
      cause,
    });
  }

  let bytes: Uint8Array;
  try {
    bytes = new Uint8Array(await file.arrayBuffer());
  } catch (cause) {
    return err({ code: 'READ_FAILED', message: 'Could not read this PDF.', cause });
  }

  let doc;
  try {
    doc = await pdfjs.getDocument({ data: bytes.slice() }).promise;
  } catch (cause) {
    return err({
      code: 'PDF_PARSE_FAILED',
      message: 'This file looks corrupt or is not a valid PDF.',
      cause,
    });
  }

  const stepError = (
    pageNum: number,
    step: string,
    cause: unknown,
  ): { code: string; message: string; cause: unknown } => {
    let detail = cause instanceof Error ? cause.message : String(cause);
    if (cause instanceof Error && cause.stack) {
      // Take the first non-empty stack line, regardless of format.
      // Chrome uses "at fn (url:line)", Safari uses "fn@url:line" — the
      // previous filter only matched Chrome and silently dropped Safari.
      const firstFrame = cause.stack
        .split('\n')
        .map((l) => l.trim())
        .find((l) => l.length > 0 && l !== detail);
      if (firstFrame) detail += ` | ${firstFrame}`;
    }
    const ua = (navigator.userAgent || '').slice(0, 80);
    return {
      code: 'PDF_PAGE_FAILED',
      message: `pdf p${String(pageNum)} · ${step}: ${detail} | ua=${ua}`,
      cause,
    };
  };

  const pages: PdfPageInfo[] = [];
  for (let i = 1; i <= doc.numPages; i++) {
    let page;
    try {
      page = await doc.getPage(i);
    } catch (cause) {
      return err(stepError(i, 'getPage', cause));
    }

    let viewport;
    try {
      viewport = page.getViewport({ scale: 1 });
    } catch (cause) {
      return err(stepError(i, 'getViewport', cause));
    }

    let content;
    try {
      content = await page.getTextContent();
    } catch (cause) {
      return err(stepError(i, 'getTextContent', cause));
    }

    let text: string, items: PdfTextItem[];
    try {
      const rawItems = Array.isArray(content.items) ? content.items : [];
      ({ text, items } = extractPageText(rawItems));
    } catch (cause) {
      return err(stepError(i, 'extractPageText', cause));
    }

    // Pull filled AcroForm field values from the page annotations and
    // append them as synthetic text items. PDFs with fillable forms
    // typically store these values in widget annotations rather than
    // the page content stream — getTextContent above misses them
    // entirely. Failure here is non-fatal; we just lose form-field
    // detection on this page.
    try {
      const annotations = await page.getAnnotations();
      const annotList = Array.isArray(annotations) ? annotations : [];
      const fields = collectFormFields(annotList);
      ({ text, items } = appendFormFieldItems({ text, items }, fields));
    } catch (err) {
      console.warn(`[anomia] PDF page ${String(i + 1)} getAnnotations failed`, err);
    }

    let namesDets: TextDetection[];
    try {
      namesDets = await detectNames(text);
    } catch (cause) {
      return err(stepError(i, 'detectNames', cause));
    }

    let regexDets: TextDetection[];
    try {
      regexDets = detectWithRegex(text);
    } catch (cause) {
      return err(stepError(i, 'detectWithRegex', cause));
    }

    let formDets: TextDetection[];
    try {
      formDets = detectFormFields(text);
    } catch (cause) {
      return err(stepError(i, 'detectFormFields', cause));
    }

    let detections: TextDetection[];
    try {
      detections = mergeDetections([...regexDets, ...formDets, ...namesDets]);
    } catch (cause) {
      return err(stepError(i, 'mergeDetections', cause));
    }

    const [vbX0 = 0, vbY0 = 0, vbX1 = 0, vbY1 = 0] = viewport.viewBox;
    pages.push({
      index: i - 1,
      widthPdf: vbX1 - vbX0,
      heightPdf: vbY1 - vbY0,
      rotation: page.rotate,
      text,
      items,
      detections,
    });
  }

  // Pages with no extractable text get an OCR pass. Catches scanned
  // PDFs and "PDF wrappers" where every page is a flattened image.
  // Serialised through the OCR module's global queue so we never run
  // more than one Tesseract worker at a time.
  const ocrCandidates = pages.filter((p) => p.text.trim().length < PAGE_OCR_THRESHOLD);
  if (ocrCandidates.length > 0) {
    opts.onProgress?.({
      kind: 'ocr-start',
      message: `scanning text in ${String(ocrCandidates.length)} image-only page${ocrCandidates.length === 1 ? '' : 's'}…`,
    });
    for (let i = 0; i < ocrCandidates.length; i++) {
      const pageInfo = ocrCandidates[i];
      if (!pageInfo) continue;
      try {
        const sourcePage = await doc.getPage(pageInfo.index + 1);
        const viewport = sourcePage.getViewport({ scale: PAGE_OCR_SCALE });
        const canvas = document.createElement('canvas');
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          sourcePage.cleanup();
          continue;
        }
        await sourcePage.render({ canvas, canvasContext: ctx, viewport }).promise;
        sourcePage.cleanup();

        const pageNum = pageInfo.index + 1;
        const cw = canvas.width;
        const ch = canvas.height;
        // Binarise scanned pages before OCR — kills bleed-through and
        // gradient backgrounds that make Tesseract miss text. Default
        // PSM (auto) is correct for dense document pages, unlike the
        // image pipeline which uses PSM 11 for sparse ID-card layouts.
        preprocessForOcr(canvas);
        const ocr = await recognize(canvas, (p) => {
          if (p.status !== 'recognizing text') return;
          opts.onProgress?.({
            kind: 'ocr-progress',
            message: `scanning page ${String(pageNum)} (${String(i + 1)}/${String(ocrCandidates.length)})… ${String(Math.round(p.progress * 100))}%`,
            fraction: p.progress,
          });
        });
        // Free the canvas backing store immediately after OCR.
        canvas.width = 0;
        canvas.height = 0;

        if (ocr.text.length >= 4) {
          const namesDets = await detectNames(ocr.text);
          const raw = [
            ...detectWithRegex(ocr.text),
            ...detectFormFields(ocr.text),
            ...namesDets,
          ];
          const merged = mergeDetections(raw);
          pageInfo.imageTextHits = liftPdfOcr(ocr.words, cw, ch, merged);
        }
      } catch (cause) {
        // Swallow per-page OCR failures — they shouldn't fail the
        // whole dispatch.
        console.warn('[anomia] PDF OCR page failed', pageInfo.index, cause);
      }
    }
    opts.onProgress?.({ kind: 'ocr-end', message: '' });
  }

  let metadata: PdfMetaResult = { fields: [] };
  try {
    const pdfLib = await loadPdfLib();
    const lib = await pdfLib.PDFDocument.load(bytes.slice(), { updateMetadata: false });
    metadata = detectPdfMetadata(lib);
  } catch {
    // metadata extraction failure is non-fatal — leave fields empty.
  }

  // Best-effort language detection on the concatenated page text.
  // Falls through silently if the lib fails to load — UI just won't
  // show the language badge.
  let language: DetectedLanguage | undefined;
  try {
    const sample = pages
      .slice(0, 5)
      .map((p) => p.text)
      .join('\n')
      .slice(0, 4000);
    language = await detectLanguage(sample);
  } catch {
    language = undefined;
  }

  return ok({
    kind: 'pdf',
    filename: file.name,
    outputFilename: outputFilename(),
    pages,
    metadata,
    pdfBytes: bytes,
    ...(language !== undefined && { language }),
  });
};

export { loadPdfjs, loadPdfLib };

/** Internal helpers exposed for unit testing. */
export const _internals = {
  collectFormFields,
  appendFormFieldItems,
  extractPageText,
};
